import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import { useFormik } from 'formik';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import FileUpload from '../../../components/FileUpload';
import FilesList from '../../../components/FilesList';
import Button from '../../../components/Button';
import DateTimeInput from '../../../components/DateTimeInput';
import LoadingSpinner from '../../../components/LoadingSpinner';

// TYPES
import { FeedbackProgressViewTypes } from './FeedbackProgressModal';
import { FeedbackLogActionCommand } from '../../../api/models';

const UpdateActivity: React.FC<FeedbackProgressViewTypes> = ({
	activity = {},
	closeModal,
	refreshFeedbackProgress,
	feedbackId
}) => {
	const { title = '', occurrence = '', description = '', attachments = [], creator = '' } = activity;
	const createAttachments: FeedbackLogActionCommand['createAttachments'] = [];
	const id = feedbackId;

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const logFeedbackProgress = async (values: FeedbackLogActionCommand) => {
		if (values !== undefined) {
			setIsLoading(true);

			const response = await apibridge.logFeedbackProgress(values);

			response.result = response.result || [];

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);
			}
		}
	};

	const logProgress = async (values: FeedbackLogActionCommand) => {
		await logFeedbackProgress(values);

		refreshFeedbackProgress(id);

		if (closeModal) {
			closeModal();
		}
	};

	const initialValues: FeedbackLogActionCommand = {
		id,
		title,
		occurrence,
		description,
		createAttachments
	};

	const formik = useFormik({
		initialValues,
		validate: (values) => {
			const errors: any = {};

			if (!values.title) {
				errors.title = 'Required';
			}

			if (!values.description) {
				errors.description = 'Required';
			}

			return errors;
		},
		onSubmit: (values) => {
			logProgress(values);
		}
	});

	return (
		<form onSubmit={formik.handleSubmit}>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="form-group">
									<label htmlFor="title">Title of Activity</label>

									<input
										className="form-control"
										name="title"
										id="title"
										type="text"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.title || ''}
										placeholder="E.g. Phone call with feedback provider"
									/>

									{formik.touched.title && formik.errors.title ? (
										<div className="form-validation-error">{formik.errors.title}</div>
									) : null}
								</div>

								<div className="form-group">
									<label htmlFor="occurrence">Date and time of the activity</label>

									<DateTimeInput
										dateTimeString={formik.values.occurrence || ''}
										onChange={(val: string) => formik.setFieldValue('occurrence', val)}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="description">Description of activity</label>

									<textarea
										className="form-control"
										name="description"
										id="description"
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.description}
									></textarea>

									{formik.touched.description && formik.errors.description ? (
										<div className="form-validation-error">{formik.errors.description}</div>
									) : null}
								</div>

								<div className="form-group">
									<label htmlFor="createAttachments">
										Attachments <span className="note">(optional)</span>
									</label>

									<FilesList
										files={attachments}
										onChange={(vals: string[]) => formik.setFieldValue('deleteAttachmentIds', vals)}
										multiple={true}
									/>

									<FileUpload
										name="createAttachments"
										onChange={(vals: string[]) => formik.setFieldValue('createAttachments', vals)}
										multiple={true}
										maxSizeMb={5}
									></FileUpload>
								</div>

								<section className="actions">
									{isOrganisationActive && (
										<Button className="btn-primary full-width" type="submit">
											Log action
										</Button>
									)}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</form>
	);
};

export default UpdateActivity;
